import {
  addDays,
  differenceInCalendarDays,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isWithinInterval,
  parse,
  startOfMonth,
  startOfYear,
} from "date-fns";
import { ru } from "date-fns/locale";

import { IvideonFaceAnalytics } from "~/entity/Ivideon";
import { fromZerotoNaN } from "~/pages/IvideonDealerSummary2/helper";
import { period } from "~/pages/IvideonDealerSummary2/options";

import { FaceDataBarState } from ".";

export const FACE_DATA_KEYS = [
  "totalFaces",
  "uniqueFaces",
  "newFaces",
  "returningFaces",
  "male",
  "female",
] as const;

type FaceKey = (typeof FACE_DATA_KEYS)[number];

export const getFaceDataYears = ({
  startDate,
  endDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getFaceDataIProps, "variant">) => {
  const parseFormat = "yyyy-MM-dd";
  const dateBarsLabel: string[] = [];

  const acc = FACE_DATA_KEYS.reduce(
    (obj, key) => {
      obj[key] = [];
      return obj;
    },
    {} as Record<FaceKey, number[]>,
  );

  const years = eachYearOfInterval({ start: startDate, end: endDate });

  years.forEach((yearStart, index) => {
    let currentRangeStart = startOfYear(yearStart);
    let currentRangeEnd = endOfYear(yearStart);

    if (index === 0) currentRangeStart = startDate;
    if (index === years.length - 1) currentRangeEnd = endDate;

    dateBarsLabel.push(format(yearStart, "yyyy"));

    const currentYearSums = {
      totalFaces: 0,
      uniqueFaces: 0,
      newFaces: 0,
      returningFaces: 0,
      male: 0,
      female: 0,
    };

    data.periodStats.forEach((item) => {
      if (!item?.periodStart) return;

      const itemDate = parse(item.periodStart, parseFormat, new Date());
      if (
        isWithinInterval(itemDate, {
          start: currentRangeStart,
          end: currentRangeEnd,
        })
      ) {
        currentYearSums.totalFaces += item.totalFaces || 0;
        currentYearSums.uniqueFaces += item.uniqueFaces || 0;
        currentYearSums.newFaces += item.newFaces || 0;
        currentYearSums.returningFaces += item.returningFaces || 0;
        currentYearSums.male += item.genderDetails?.male || 0;
        currentYearSums.female += item.genderDetails?.female || 0;
      }
    });

    FACE_DATA_KEYS.forEach((key) => {
      acc[key].push(currentYearSums[key]);
    });
  });

  setLabels(dateBarsLabel);

  const formattedData = FACE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as Partial<FaceDataBarState>);

  setDataBar({
    ...formattedData,
    ageDetails: data.totalAgeDetails,
    totalEmotionDetails: data.totalEmotionDetails,
    visitTimeDetails: data.totalVisitTimeDetails,
    overallAverageDwellTime: data.overallAverageDwellTime,
  } as FaceDataBarState);
};

export const getFaceDataMonths = ({
  startDate,
  endDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getFaceDataIProps, "variant">) => {
  const parseFormat = "yyyy-MM-dd";
  const dateBarsLabel: string[][] = [];

  const acc = FACE_DATA_KEYS.reduce(
    (obj, key) => {
      obj[key] = [];
      return obj;
    },
    {} as Record<FaceKey, number[]>,
  );

  const months = eachMonthOfInterval({ start: startDate, end: endDate });

  months.forEach((monthStart, index) => {
    let currentRangeStart = startOfMonth(monthStart);
    let currentRangeEnd = endOfMonth(monthStart);

    if (index === 0) currentRangeStart = startDate;
    if (index === months.length - 1) currentRangeEnd = endDate;

    dateBarsLabel.push([
      format(monthStart, "LLLL", { locale: ru }),
      format(monthStart, "yyyy"),
    ]);

    const currentMonthSums = {
      totalFaces: 0,
      uniqueFaces: 0,
      newFaces: 0,
      returningFaces: 0,
      male: 0,
      female: 0,
    };

    data.periodStats.forEach((item) => {
      if (!item?.periodStart) return;

      const itemDate = parse(item.periodStart, parseFormat, new Date());
      if (
        isWithinInterval(itemDate, {
          start: currentRangeStart,
          end: currentRangeEnd,
        })
      ) {
        currentMonthSums.totalFaces += item.totalFaces || 0;
        currentMonthSums.uniqueFaces += item.uniqueFaces || 0;
        currentMonthSums.newFaces += item.newFaces || 0;
        currentMonthSums.returningFaces += item.returningFaces || 0;
        currentMonthSums.male += item.genderDetails?.male || 0;
        currentMonthSums.female += item.genderDetails?.female || 0;
      }
    });

    FACE_DATA_KEYS.forEach((key) => {
      acc[key].push(currentMonthSums[key]);
    });
  });

  setLabels(dateBarsLabel);

  const formattedData = FACE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as Partial<FaceDataBarState>);

  setDataBar({
    ...formattedData,
    ageDetails: data.totalAgeDetails,
    totalEmotionDetails: data.totalEmotionDetails,
    visitTimeDetails: data.totalVisitTimeDetails,
    overallAverageDwellTime: data.overallAverageDwellTime,
  } as FaceDataBarState);
};

export const getFaceDataWeeks = ({
  startDate,
  endDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getFaceDataIProps, "variant">) => {
  const parseFormat = "yyyy-MM-dd";
  const dateBarsLabel: string[][] = [];

  const acc = FACE_DATA_KEYS.reduce(
    (obj, key) => {
      obj[key] = [];
      return obj;
    },
    {} as Record<FaceKey, number[]>,
  );

  const weeks = eachWeekOfInterval(
    { start: startDate, end: endDate },
    { weekStartsOn: 1 },
  );

  weeks.forEach((weekStart, index) => {
    let currentRangeStart = weekStart;
    let currentRangeEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

    if (index === 0) currentRangeStart = startDate;
    if (index === weeks.length - 1) currentRangeEnd = endDate;

    dateBarsLabel.push([
      format(currentRangeStart, "dd.MM", { locale: ru }),
      format(currentRangeEnd, "dd.MM"),
    ]);

    const currentWeekSums = {
      totalFaces: 0,
      uniqueFaces: 0,
      newFaces: 0,
      returningFaces: 0,
      male: 0,
      female: 0,
    };

    data.periodStats.forEach((item) => {
      if (!item?.periodStart) return;

      const itemDate = parse(item.periodStart, parseFormat, new Date());
      const isInside = isWithinInterval(itemDate, {
        start: currentRangeStart,
        end: currentRangeEnd,
      });

      if (isInside) {
        currentWeekSums.totalFaces += item.totalFaces || 0;
        currentWeekSums.uniqueFaces += item.uniqueFaces || 0;
        currentWeekSums.newFaces += item.newFaces || 0;
        currentWeekSums.returningFaces += item.returningFaces || 0;
        currentWeekSums.male += item.genderDetails?.male || 0;
        currentWeekSums.female += item.genderDetails?.female || 0;
      }
    });

    FACE_DATA_KEYS.forEach((key) => {
      acc[key].push(currentWeekSums[key]);
    });
  });

  setLabels(dateBarsLabel);

  const formattedData = FACE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as Partial<FaceDataBarState>);

  setDataBar({
    ...formattedData,
    ageDetails: data.totalAgeDetails,
    totalEmotionDetails: data.totalEmotionDetails,
    visitTimeDetails: data.totalVisitTimeDetails,
    overallAverageDwellTime: data.overallAverageDwellTime,
  } as FaceDataBarState);
};

export const getFaceDataDays = ({
  difference,
  startDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getFaceDataIProps, "endDate" | "variant"> & { difference: number }) => {
  const dateBarsLabel: string[][] = [];

  const statsMap = new Map(
    data.periodStats.filter(Boolean).map((s) => [s.periodStart, s]),
  );

  const acc = FACE_DATA_KEYS.reduce(
    (obj, key) => {
      obj[key] = [];
      return obj;
    },
    {} as Record<FaceKey, number[]>,
  );

  for (let i = 0; i < difference; i++) {
    const currentDate = addDays(new Date(startDate), i);
    const labelCompare = format(currentDate, "yyyy-MM-dd");

    dateBarsLabel.push([format(currentDate, "dd.MM")]);

    const stats = statsMap.get(labelCompare);

    acc.totalFaces.push(stats?.totalFaces || 0);
    acc.uniqueFaces.push(stats?.uniqueFaces || 0);
    acc.newFaces.push(stats?.newFaces || 0);
    acc.returningFaces.push(stats?.returningFaces || 0);
    acc.male.push(stats?.genderDetails?.male || 0);
    acc.female.push(stats?.genderDetails?.female || 0);
  }

  setLabels(dateBarsLabel);

  const formattedData = FACE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as Partial<FaceDataBarState>);

  setDataBar({
    ...formattedData,
    ageDetails: data.totalAgeDetails,
    totalEmotionDetails: data.totalEmotionDetails,
    visitTimeDetails: data.totalVisitTimeDetails,
    overallAverageDwellTime: data.overallAverageDwellTime,
  } as FaceDataBarState);
};

export const getFaceDataHours = ({
  data,
  setDataBar,
  setLabels,
}: Omit<getFaceDataIProps, "startDate" | "endDate" | "variant">) => {
  const dateBarsLabel: string[] = [];

  const acc = FACE_DATA_KEYS.reduce(
    (obj, key) => {
      obj[key] = [];
      return obj;
    },
    {} as Record<FaceKey, number[]>,
  );

  data.periodStats.forEach((item) => {
    if (!item) return;

    dateBarsLabel.push(`${String(item.period).padStart(2, "0")}:00`);

    acc.totalFaces.push(item.totalFaces || 0);
    acc.uniqueFaces.push(item.uniqueFaces || 0);
    acc.newFaces.push(item.newFaces || 0);
    acc.returningFaces.push(item.returningFaces || 0);
    acc.male.push(item.genderDetails?.male || 0);
    acc.female.push(item.genderDetails?.female || 0);
  });

  setLabels(dateBarsLabel);

  const formattedData = FACE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as Partial<FaceDataBarState>);

  setDataBar({
    ...formattedData,
    ageDetails: data.totalAgeDetails,
    totalEmotionDetails: data.totalEmotionDetails,
    visitTimeDetails: data.totalVisitTimeDetails,
    overallAverageDwellTime: data.overallAverageDwellTime,
  } as FaceDataBarState);
};

export interface getFaceDataIProps {
  startDate: Date;
  endDate: Date;
  data: IvideonFaceAnalytics;
  setDataBar: (props: FaceDataBarState) => void;
  setLabels: (props: string[] | string[][]) => void;
}

export const getFaceData = ({
  startDate,
  endDate,
  data,
  setDataBar,
  setLabels,
}: getFaceDataIProps) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

  const difference = differenceInCalendarDays(end, start) + 1;

  const params = {
    difference,
    startDate: start,
    endDate: end,
    data,
    setDataBar,
    setLabels,
  };

  if (difference >= period.year) {
    getFaceDataYears(params);
  } else if (difference >= period.month && difference < period.year) {
    getFaceDataMonths(params);
  } else if (difference <= period.week && difference >= period.day) {
    getFaceDataWeeks(params);
  } else if (difference < period.day && difference !== period.hour) {
    getFaceDataDays(params);
  } else if (difference === period.hour) {
    getFaceDataHours({ data, setDataBar, setLabels });
  }
};
